import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Structures} from "@clavisco/core";
import ICLResponse = Structures.Interfaces.ICLResponse;
import {Observable, tap} from "rxjs";
import {IUser} from "../interfaces/user";
import {SharedService} from "../shared/shared-service.service";
import { IPermissionsByUser, IPermissionsUser } from '../interfaces/base-entity';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private readonly CONTROLLER = 'api/Users'
  constructor(private http: HttpClient, private sharedService: SharedService) { }

  /**
   * Retrieves a list of users.
   *
   * @returns {Observable<ICLResponse<IUser[]>>} An observable containing the response with the list of users.
   */
  Get(): Observable<ICLResponse<IUser[]>>{
    return this.http.get<ICLResponse<IUser[]>>(`${this.CONTROLLER}`, {headers: this.sharedService.DefineDescriptionHeader({OnError: `Error obteniendo usuario`})});
  }

  /**
   * Retrieves a user by its ID.
   *
   * @param {number} Id - The ID of the user to retrieve.
   * @returns {Observable<ICLResponse<IUser>>} An observable containing the response with the user data.
   */
  GetId(Id : number): Observable<ICLResponse<IUser>>{
    return this.http.get<ICLResponse<IUser>>(`${this.CONTROLLER}/${Id}`, { headers: this.sharedService.DefineDescriptionHeader({
        OnError: `Error obteniendo usuario ${Id}`
      })});
  }

  /**
   * Retrieves users based on name and status filters.
   *
   * @param {string} name - The name to filter users by.
   * @param {number} status - The status to filter users by.
   * @returns {Observable<ICLResponse<IUser[]>>} An observable containing the response with the list of users that match the filters.
   */
  GetFilters(name: string, status: number): Observable<ICLResponse<IUser[]>>{
    let params = {
      status: status,
      name: name
    }
    return this.http.get<ICLResponse<IUser[]>>(`${this.CONTROLLER}/Filters`, {params, headers: this.sharedService.DefineDescriptionHeader({
        OnError: `Error obteniendo usuario acorde a filtros`,
      })});
  }

  /**
   * Creates a new user.
   *
   * @param {IUser} user - The user data to create.
   * @returns {Observable<ICLResponse<IUser>>} An observable containing the response with the created user.
   */
  Post(user: IUser): Observable<ICLResponse<IUser>> {
    return this.http.post<ICLResponse<IUser>>(`${this.CONTROLLER}`, user, {headers: this.sharedService.DefineDescriptionHeader({
        OnError: `Error al crear del usuario`,
        OnSuccess: `Creacion de usuario exitosa`
      })});
  }

  /**
   * Updates an existing user.
   *
   * @param {IUser} user - The user data to update.
   * @returns {Observable<ICLResponse<IUser>>} An observable containing the response with the updated user.
   */
  Patch(user: IUser): Observable<ICLResponse<IUser>> {
    return this.http.patch<ICLResponse<IUser>>(`${this.CONTROLLER}`, user, {headers: this.sharedService.DefineDescriptionHeader({
        OnError: `Error al actualizar usuario`,
        OnSuccess: `Actualizacion de usuario exitoso`
      })});
  }


    /**
   * Updates permissions associated with a specific role.
   *
   * @param {number[]} permissionIds - The IDs of the permissions to update.
   * @param {number} roleId - The ID of the role to update permissions for.
   * @returns {Observable<ICLResponse<IPermissionsByRole>>} An observable containing the response with the status of the update operation.
   */
  PatchPermissionsByRole(permissionIds: number[], userId: number):Observable<ICLResponse<IPermissionsByUser>>{
    let params = {
      PermissionsIds: permissionIds,
      UserId: userId
    }
    return this.http.patch<ICLResponse<IPermissionsByUser>>(`${this.CONTROLLER}/PatchPermissionsByUser`, params, {headers: this.sharedService.DefineDescriptionHeader({
        OnError: `Error al actualizar permisos del role ${userId}`,
        OnSuccess: `Actualizacion de permisos del role ${userId} exitoso`
      })});
    }
  
  /**
   * Retrieves permissions associated with a specific role and application code.
   *
   * @param {number} userId - The ID of the user to get permissions for.
   * @param {string} appCode - The application code to get permissions for.
   * @returns {Observable<ICLResponse<IPermissionsRoles>>} An observable containing the response with the permissions for the specified user and application code.
   */
  GetPermissionsUsers(userId: number):Observable<ICLResponse<IPermissionsUser>>{
    let params = {
      userId: userId
    }
    return this.http.get<ICLResponse<IPermissionsUser>>(`${this.CONTROLLER}/GetPermissionsUser`, {params, headers: this.sharedService.DefineDescriptionHeader({
        OnError: `Error al obtener permisos del usuario`
      })});
  }
}
